import React, { useCallback, useEffect, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FileXls, FilePdf, ClipboardText, Warning } from "@phosphor-icons/react";

const STATUS_STYLE = {
  "HABIS": "bg-red-100 text-red-800 border-red-400",
  "OVER QUOTA": "bg-destructive text-destructive-foreground border-destructive",
  "STOK RENDAH": "bg-amber-100 text-amber-800 border-amber-500",
  "AMAN": "bg-emerald-100 text-emerald-800 border-emerald-500",
};

export default function SORemainingReport() {
  const [plants, setPlants] = useState([]);
  const [products, setProducts] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [filters, setFilters] = useState({
    distrik_id: "__any__", plant_id: "__any__", produk_id: "__any__",
    only_low: false, include_inactive: false,
  });
  const [data, setData] = useState({ rows: [], summary: {} });
  const [loading, setLoading] = useState(false);
  const loadToken = React.useRef(0);

  const load = useCallback(async () => {
    const my = ++loadToken.current;
    setLoading(true);
    try {
      const params = { only_low: filters.only_low, include_inactive: filters.include_inactive };
      if (filters.distrik_id !== "__any__") params.distrik_id = filters.distrik_id;
      if (filters.plant_id !== "__any__") params.plant_id = filters.plant_id;
      if (filters.produk_id !== "__any__") params.produk_id = filters.produk_id;
      const { data: d } = await api.get("/reports/so-remaining", { params });
      if (my !== loadToken.current) return;
      setData(d);
    } catch {
      if (my === loadToken.current) setData({ rows: [], summary: {} });
    } finally {
      if (my === loadToken.current) setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    Promise.all([
      api.get("/plants", { params: { aktif: true } }),
      api.get("/products", { params: { aktif: true } }),
      api.get("/districts", { params: { aktif: true } }),
    ]).then(([pl, p, d]) => { setPlants(pl.data); setProducts(p.data); setDistricts(d.data); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const buildQS = () => {
    const p = new URLSearchParams();
    if (filters.distrik_id !== "__any__") p.set("distrik_id", filters.distrik_id);
    if (filters.plant_id !== "__any__") p.set("plant_id", filters.plant_id);
    if (filters.produk_id !== "__any__") p.set("produk_id", filters.produk_id);
    if (filters.only_low) p.set("only_low", "true");
    if (filters.include_inactive) p.set("include_inactive", "true");
    return p.toString();
  };

  const downloadExport = async (format) => {
    const qs = buildQS();
    const url = `${API_BASE}/reports/so-remaining/export.${format}${qs ? `?${qs}` : ""}`;
    try {
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `laporan_sisa_so.${format === "xlsx" ? "xlsx" : "pdf"}`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success(`Export ${format.toUpperCase()} berhasil`);
    } catch (e) { toast.error(`Export gagal: ${e.message}`); }
  };

  const s = data.summary || {};
  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-8 space-y-6" data-testid="so-remaining-page">
      <div className="border-b border-border pb-4">
        <div className="flex items-center gap-3">
          <ClipboardText size={28} weight="duotone" className="text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Laporan Sisa SO</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Stok SO tersisa per plant/produk/distrik. SO yang habis atau stok rendah muncul di atas.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="border border-border bg-secondary/20 p-4 space-y-3" data-testid="filters-card">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FilterSelect label="Plant" testid="filter-plant" value={filters.plant_id}
                        onChange={(v) => setFilters((f) => ({ ...f, plant_id: v }))} options={plants} />
          <FilterSelect label="Produk" testid="filter-produk" value={filters.produk_id}
                        onChange={(v) => setFilters((f) => ({ ...f, produk_id: v }))} options={products} />
          <FilterSelect label="Distrik" testid="filter-distrik" value={filters.distrik_id}
                        onChange={(v) => setFilters((f) => ({ ...f, distrik_id: v }))} options={districts} />
        </div>
        <div className="flex flex-wrap items-center gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={filters.only_low} onCheckedChange={(v) => setFilters((f) => ({ ...f, only_low: !!v }))} data-testid="filter-only-low" />
            <span>Hanya SO habis / stok rendah</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={filters.include_inactive} onCheckedChange={(v) => setFilters((f) => ({ ...f, include_inactive: !!v }))} data-testid="filter-include-inactive" />
            <span>Sertakan SO non-aktif</span>
          </label>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => downloadExport("xlsx")} className="rounded-none" data-testid="export-xlsx-btn">
            <FileXls size={16} className="mr-2" /> Export Excel
          </Button>
          <Button variant="outline" onClick={() => downloadExport("pdf")} className="rounded-none" data-testid="export-pdf-btn">
            <FilePdf size={16} className="mr-2" /> Export PDF
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="summary-chips">
        <Chip label="Total SO" value={s.row_count ?? 0} />
        <Chip label="Total Sisa" value={s.total_sisa ?? 0} />
        <Chip label="Habis" value={s.habis_count ?? 0} tone="red" />
        <Chip label="Over Quota" value={s.over_quota_count ?? 0} tone="destructive" />
        <Chip label="Stok Rendah" value={s.low_stock_count ?? 0} tone="amber" />
      </div>

      {/* Table */}
      <div className="border border-border overflow-x-auto" data-testid="so-table">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 border-b border-border">
            <tr className="text-left">
              <th className="px-3 py-2 overline">No SO</th>
              <th className="px-3 py-2 overline">Plant</th>
              <th className="px-3 py-2 overline">Produk</th>
              <th className="px-3 py-2 overline">Distrik</th>
              <th className="px-3 py-2 overline text-right">Total</th>
              <th className="px-3 py-2 overline text-right">Terpakai</th>
              <th className="px-3 py-2 overline text-right">Sisa</th>
              <th className="px-3 py-2 overline text-right">Min Reorder</th>
              <th className="px-3 py-2 overline">Status</th>
              <th className="px-3 py-2 overline">Aktif</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Memuat…</td></tr>}
            {!loading && data.rows.length === 0 && (
              <tr><td colSpan={10} className="text-center py-8 text-muted-foreground">Tidak ada SO untuk filter ini.</td></tr>
            )}
            {data.rows.map((r, i) => (
              <tr key={r.id} className="border-b border-border/40 hover:bg-secondary/30" data-testid={`so-row-${i}`}>
                <td className="px-3 py-2 font-mono font-bold">{r.no_so}</td>
                <td className="px-3 py-2">{r.plant_nama || "-"}</td>
                <td className="px-3 py-2">{r.produk_nama || "-"}</td>
                <td className="px-3 py-2">{r.distrik_nama || "-"}</td>
                <td className="px-3 py-2 font-mono text-right">{r.kuantiti_total}</td>
                <td className="px-3 py-2 font-mono text-right">{r.kuantiti_terpakai}</td>
                <td className={`px-3 py-2 font-mono text-right font-bold ${r.kuantiti_sisa <= 0 ? "text-destructive" : ""}`}>
                  {r.kuantiti_sisa}
                </td>
                <td className="px-3 py-2 font-mono text-right text-muted-foreground">{r.min_reorder}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center gap-1 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLE[r.status] || ""}`}>
                    {r.status === "HABIS" || r.status === "OVER QUOTA" ? <Warning size={10} weight="bold" /> : null}
                    {r.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-xs">{r.aktif ? "YA" : "TIDAK"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label className="overline block mb-1">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-none h-10" data-testid={testid}>
          <SelectValue placeholder={`Semua ${label}`} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__any__">Semua {label}</SelectItem>
          {options.map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.nama}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Chip({ label, value, tone }) {
  const toneCls = tone === "red" ? "text-red-600" :
                  tone === "destructive" ? "text-destructive" :
                  tone === "amber" ? "text-amber-600" : "";
  return (
    <div className="border border-border bg-background p-3">
      <div className="overline text-muted-foreground text-[10px]">{label}</div>
      <div className={`text-2xl font-bold font-mono mt-1 ${toneCls}`}>{value}</div>
    </div>
  );
}
